import { memo, type Ref, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

import { copyLastPackageEntry, deletePackageEntry } from "~/features/cart/cart/redux/actions/cartPageActions";
import { deleteProductSaga } from "~/features/cart/cart/redux/actions/cartQuoteCommonActions";
import type { CartEntryRowViewModel, ProductTableViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";
import ProductEntryList, { type ProductEntryListRef } from "~/features/cart/cartV2/ProductEntryList";
import { useSessionState } from "~/app/providers/SessionProvider";

interface InsertAlternateRowInput {
  anchorProductGroupNumber: number | null;
  productCode: string;
  designation: string;
}

interface ProductTableProps {
  viewModel: ProductTableViewModel;
  onClear?: () => void;
  "data-test-id"?: string;
  scrollRef?: Ref<ProductEntryListRef>;
  onInsertAlternateRow?: (input: InsertAlternateRowInput) => void;
  onCloseAlternateRow?: (rowId: string) => void;
}

const ProductTable = (props: ProductTableProps) => {
  const { viewModel, "data-test-id": testId, onInsertAlternateRow, onCloseAlternateRow, scrollRef } = props;
  const dispatch = useDispatch();
  const { cartActionsDisabled } = useSessionState();

  const entryPositionByProductGroupNumber = useMemo(
    () =>
      new Map(
        viewModel.rows
          .filter((row): row is CartEntryRowViewModel => row.kind === "entry")
          .map((row, index) => [row.productGroupNumber, index]),
      ),
    [viewModel.rows],
  );
  const entryRowByCartEntryId = useMemo(
    () =>
      new Map(
        viewModel.rows
          .filter((row): row is CartEntryRowViewModel => row.kind === "entry")
          .map((row) => [row.cartEntryId, row]),
      ),
    [viewModel.rows],
  );
  const entryRowByCopySourceId = useMemo(
    () =>
      new Map(
        viewModel.rows
          .filter((row): row is CartEntryRowViewModel => row.kind === "entry")
          .filter((row) => row.details.copySourcePackEntryId !== null)
          .map((row) => [row.details.copySourcePackEntryId!, row]),
      ),
    [viewModel.rows],
  );

  const getAlternateInsertPosition = useCallback(
    (anchor: number | null) => {
      if (anchor === null) {
        return undefined;
      }

      return entryPositionByProductGroupNumber.get(anchor);
    },
    [entryPositionByProductGroupNumber],
  );

  const handleCloseAlternateRow = useCallback(
    (rowId: string) => {
      onCloseAlternateRow?.(rowId);
    },
    [onCloseAlternateRow],
  );

  const handleDeleteProduct = useCallback(
    (cartEntryId: number) => {
      if (cartActionsDisabled) {
        return;
      }

      const row = entryRowByCartEntryId.get(cartEntryId);

      if (!row?.mainDescriptor || row.phaseout?.hasLockedLines) {
        return;
      }

      onInsertAlternateRow?.({
        anchorProductGroupNumber: row.productGroupNumber,
        productCode: row.mainDescriptor.base.code,
        designation: row.mainDescriptor.base.designation,
      });
      dispatch(deleteProductSaga(row.mainDescriptor.base.code));
    },
    [cartActionsDisabled, dispatch, entryRowByCartEntryId, onInsertAlternateRow],
  );

  const handleCopyLine = useCallback(
    (packEntryId: number) => {
      if (cartActionsDisabled) {
        return;
      }

      const row = entryRowByCopySourceId.get(packEntryId);
      if (!row?.details.canCopyLine || row.phaseout?.hasLockedLines) {
        return;
      }
      dispatch(copyLastPackageEntry(packEntryId));
    },
    [cartActionsDisabled, dispatch, entryRowByCopySourceId],
  );

  const handleDeleteLine = useCallback(
    ({ cartEntryId, lineItemId }: { cartEntryId: number; lineItemId: number }) => {
      if (cartActionsDisabled) {
        return;
      }

      const row = entryRowByCartEntryId.get(cartEntryId);

      if (!row) {
        return;
      }

      if (row.phaseout?.hasLockedLines) {
        return;
      }

      const line = row.details.lines.find((candidate) => candidate.lineItemId === lineItemId);
      const descriptor = line?.descriptor ?? row.mainDescriptor;

      if (line && !line.flags.inStock && row.summary.canDeleteProduct && descriptor) {
        onInsertAlternateRow?.({
          anchorProductGroupNumber: row.productGroupNumber,
          productCode: descriptor.base.code,
          designation: descriptor.base.designation,
        });
      }

      dispatch(deletePackageEntry(lineItemId));
    },
    [cartActionsDisabled, dispatch, entryRowByCartEntryId, onInsertAlternateRow],
  );

  return (
    <ProductEntryList
      rows={viewModel.rows}
      editMode={viewModel.mode === "edit" && !cartActionsDisabled}
      cartActionsDisabled={cartActionsDisabled}
      scrollRef={scrollRef}
      data-test-id={testId}
      onCloseAlternateRow={handleCloseAlternateRow}
      onDeleteProduct={handleDeleteProduct}
      onDeleteLine={handleDeleteLine}
      onCopyLine={handleCopyLine}
      getAlternateInsertPosition={getAlternateInsertPosition}
    />
  );
};

export default memo(ProductTable);
